import { createElement } from "react";
import { createRoute, lazyRouteComponent } from "@tanstack/react-router";

import { requirePermission } from "../../../../shared/routing/requirePermission";
import {
  ADMINS_CREATE,
  ADMINS_UPDATE,
  ADMINS_VIEW,
} from "../../organizationPermissions";
import {
  adminsSearchSchema,
  archivedAdminsSearchSchema,
} from "./searchSchemas";
import type { OrganizationRouteFactory } from "./organizationRouteContext";

const AccountAdminDetailScreen = lazyRouteComponent(
  () => import("../AccountAdminDetailScreen"),
  "AccountAdminDetailScreen",
);
const AccountAdminFormScreen = lazyRouteComponent(
  () => import("../AccountAdminFormScreen"),
  "AccountAdminFormScreen",
);
const AdminsListScreen = lazyRouteComponent(
  () => import("../AdminsListScreen"),
  "AdminsListScreen",
);
const ArchivedAdminsListScreen = lazyRouteComponent(
  () => import("../ArchivedAdminsListScreen"),
  "ArchivedAdminsListScreen",
);

// 'create' route must come before '$id' to avoid capturing /admins/create as an admin id
export const createAdminsRoutes: OrganizationRouteFactory = ({
  context,
  companies,
  accountAdmins,
}) => {
  const editRoute = createRoute({
    getParentRoute: context.getParentRoute,
    path: `${context.basePath}/admins/$id/edit`,
    beforeLoad: requirePermission([ADMINS_UPDATE]),
    component: function AdminEditRouteScreen() {
      // Route-scoped params, not a manual pathname split: during the
      // navigate-away transition after a successful update, the global
      // location already points at the new URL while this route's own match
      // (and its params) only change once this component actually unmounts,
      // so it never briefly re-renders with the wrong id. basePath is a
      // runtime string, so the path template loses the literal type
      // TanStack Router needs to type params -- $id is guaranteed by the
      // route path itself.
      const { id } = editRoute.useParams() as unknown as { id: string };
      return createElement(AccountAdminFormScreen, {
        repository: accountAdmins,
        companiesRepository: companies,
        basePath: context.basePath,
        adminId: id,
      });
    },
  });

  const detailRoute = createRoute({
    getParentRoute: context.getParentRoute,
    path: `${context.basePath}/admins/$id`,
    beforeLoad: requirePermission([ADMINS_VIEW]),
    component: function AdminDetailRouteScreen() {
      const { id } = detailRoute.useParams() as unknown as { id: string };
      return createElement(AccountAdminDetailScreen, {
        repository: accountAdmins,
        basePath: context.basePath,
        adminId: id,
      });
    },
  });

  return [
    createRoute({
      getParentRoute: context.getParentRoute,
      path: `${context.basePath}/admins`,
      validateSearch: adminsSearchSchema,
      beforeLoad: requirePermission([ADMINS_VIEW]),
      component: () =>
        createElement(AdminsListScreen, {
          repository: accountAdmins,
          companiesRepository: companies,
          basePath: context.basePath,
        }),
    }),
    createRoute({
      getParentRoute: context.getParentRoute,
      path: `${context.basePath}/admins/create`,
      beforeLoad: requirePermission([ADMINS_CREATE]),
      component: () =>
        createElement(AccountAdminFormScreen, {
          repository: accountAdmins,
          companiesRepository: companies,
          basePath: context.basePath,
        }),
    }),
    createRoute({
      getParentRoute: context.getParentRoute,
      path: `${context.basePath}/admins/archived`,
      validateSearch: archivedAdminsSearchSchema,
      beforeLoad: requirePermission([ADMINS_VIEW]),
      component: () =>
        createElement(ArchivedAdminsListScreen, {
          repository: accountAdmins,
          basePath: context.basePath,
        }),
    }),
    editRoute,
    detailRoute,
  ];
};
